import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Store } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', backgroundColor: 'white' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          backgroundColor: 'white',
          color: '#050206',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 4, md: 6 }, py: 1.5 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 }, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box
              component="img"
              src="/logo.png"
              alt="DiscountDays Logo"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                mr: 1.5,
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: '#3B3B3B',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              DiscountDays
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 3, mx: 4 }}>
                <Button 
                  onClick={() => navigate('/features')} 
                  sx={{ color: '#050206', textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}
                >
                  Features
                </Button>
                <Button 
                  onClick={() => navigate('/about')} 
                  sx={{ color: '#050206', textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}
                >
                  About Us
                </Button>
                <Button 
                  onClick={() => navigate('/contact')} 
                  sx={{ color: '#050206', textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}
                >
                  Contact
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained" 
                  sx={{ 
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    '&:hover': { backgroundColor: '#0066CC' },
                  }}
                  onClick={() => navigate('/login')}
                >
                  Admin Login
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={() => setMobileOpen(true)} sx={{ ml: 'auto' }}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
          <Box sx={{ p: 3, width: 280 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#3B3B3B' }}>Menu</Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/features'); setMobileOpen(false); }}>
                  <ListItemText primary="Features" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/about'); setMobileOpen(false); }}>
                  <ListItemText primary="About Us" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/contact'); setMobileOpen(false); }}>
                  <ListItemText primary="Contact" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                >
                  Admin Login
                </Button>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </AppBar>

      {/* Delete Account Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant={isMobile ? 'h3' : 'h2'} component="h1" sx={{ fontWeight: 700, color: '#d32f2f', mb: 2 }}>
          Delete Account & Data
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
          Last Updated: November 28, 2025
        </Typography>

        <Typography paragraph>
          This page explains how you can delete your DiscountDays account and what happens to your data when you do so.
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mt: 4, mb: 2 }}>
          1. How to Delete Your Account
        </Typography>
        <Typography paragraph>
          You can request deletion of your DiscountDays account directly from within the mobile app. Follow these steps:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>Open the <strong>DiscountDays</strong> app on your device.</li>
          <li>Go to the <strong>Profile</strong> page (account/profile section).</li>
          <li>Select the option <strong>Delete Account</strong> or <strong>Request Account Deletion</strong>.</li>
          <li>Confirm the deletion request when prompted.</li>
        </Box>
        <Typography paragraph>
          Once you confirm, your account will be scheduled for deletion and you will be logged out of the app.
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mt: 4, mb: 2 }}>
          2. What Data Is Deleted
        </Typography>
        <Typography paragraph>
          When you delete your account, we permanently delete or anonymize the following data associated with your profile:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>Account details (name, email, phone number, profile picture).</li>
          <li>Login and authentication information.</li>
          <li>Personal preferences stored in your profile.</li>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mt: 4, mb: 2 }}>
          3. What Data May Be Retained
        </Typography>
        <Typography paragraph>
          In some cases, we may retain limited information for legal, security, or operational reasons, for example:
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 2 }}>
          <li>Records required to comply with applicable laws and regulations.</li>
          <li>Aggregated or anonymized data that cannot be linked back to you personally.</li>
          <li>Transaction or activity logs needed for fraud prevention or abuse detection.</li>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mt: 4, mb: 2 }}>
          4. Re-activating or Creating a New Account
        </Typography>
        <Typography paragraph>
          Once your account is deleted, it cannot be restored. If you wish to use DiscountDays again, you will need to create a new account and set up your profile from scratch.
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', mt: 4, mb: 2 }}>
          5. Contact Us About Account Deletion
        </Typography>
        <Typography paragraph>
          If you are unable to delete your account from the app, or if you have any questions about how your data is handled during deletion, you can contact us for help.
        </Typography>
        <Box sx={{ backgroundColor: '#f9f9f9', p: 3, borderRadius: 2, mt: 3 }}>
          <Typography paragraph sx={{ mb: 1 }}>
            <strong>Email:</strong> <a href="mailto:privacy@godiscountdays.com" style={{ color: '#d32f2f' }}>privacy@godiscountdays.com</a>
          </Typography>
          <Typography paragraph sx={{ mb: 0 }}>
            <strong>Website:</strong> <a href="https://godiscountdays.com" style={{ color: '#d32f2f' }}>https://godiscountdays.com</a>
          </Typography>
        </Box>

        <Typography sx={{ mt: 6, pt: 4, borderTop: '2px solid #eee', textAlign: 'center', color: '#666' }}>
          © 2025 DiscountDays. All rights reserved.
        </Typography>
      </Container>

      {/* Footer */}
      <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #eee', color: '#050206', py: { xs: 6, md: 8 }, px: { xs: 2, sm: 4 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: { xs: 4, md: 6 }, mb: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  component="img"
                  src="/logo.png"
                  alt="DiscountDays Logo"
                  sx={{ width: 40, height: 40, objectFit: 'contain', mr: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0084FF' }}>DiscountDays</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>© copyright DiscountDays 2025. All rights reserved.</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 3, sm: 6 }, flex: { xs: '1 1 100%', md: '1 1 60%' } }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Socials</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Instagram</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>LinkedIn</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Legal</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" component="a" href="/privacy-policy" sx={{ color: 'rgba(0, 0, 0, 0.6)', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Privacy Policy</Typography>
                  <Typography variant="body2" component="a" href="/delete-account" sx={{ color: 'rgba(0, 0, 0, 0.6)', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Delete Account</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Become a Partner</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Sign Up</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }} onClick={() => navigate('/login')}>Login</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default DeleteAccountPage;







